import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { PenLine, ImagePlus, X } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"];

export default function ManualAddDialog({ collections = [], onAdded }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [setNameField, setSetNameField] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [gameType, setGameType] = useState("other");
  const [condition, setCondition] = useState("NM");
  const [quantity, setQuantity] = useState("1");
  const [price, setPrice] = useState("");
  const [collectionId, setCollectionId] = useState("none");
  const [photo, setPhoto] = useState(null);
  const photoInputRef = useRef(null);

  const reset = () => {
    setName(""); setSetNameField(""); setCardNumber(""); setGameType("other");
    setCondition("NM"); setQuantity("1"); setPrice(""); setCollectionId("none"); setPhoto(null);
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Card name is required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      if (setNameField.trim()) fd.append("set_name", setNameField.trim());
      if (cardNumber.trim()) fd.append("card_number", cardNumber.trim());
      fd.append("game_type", gameType);
      fd.append("condition", condition);
      fd.append("quantity", String(Math.max(1, parseInt(quantity, 10) || 1)));
      if (collectionId !== "none") fd.append("collection_id", collectionId);
      if (price !== "" && !isNaN(parseFloat(price))) fd.append("base_price_nm", price);
      if (photo) fd.append("photo", photo);
      await api.post("/cards/manual", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`"${name.trim()}" added to your binder`);
      reset();
      setOpen(false);
      onAdded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add card. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fieldLabel = "text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block";
  const fieldInput = "bg-black/40 border-white/10";

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="binder-manual-add-button" variant="outline" className="border-[#A78BFA]/40 text-[#A78BFA] hover:bg-[#A78BFA]/10 rounded-full">
          <PenLine className="w-4 h-4 mr-2" /> Add card
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="manual-add-dialog" className="bg-[#111113] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Add a card manually</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            Type the details yourself — a photo is optional. After saving you can use the AI on the
            card's page to identify it, find a web match and pull last-sold pricing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-4">
            {photo ? (
              <div className="relative w-24 shrink-0">
                <img src={URL.createObjectURL(photo)} alt="preview" className="w-24 aspect-[3/4] object-cover rounded-lg border border-white/10" />
                <button
                  data-testid="manual-add-remove-photo"
                  onClick={() => setPhoto(null)}
                  className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black border border-white/20 flex items-center justify-center text-zinc-400 hover:text-white"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                data-testid="manual-add-photo-button"
                onClick={() => photoInputRef.current?.click()}
                className="w-24 aspect-[3/4] shrink-0 flex flex-col items-center justify-center gap-1.5 border border-dashed border-white/20 rounded-lg text-zinc-500 hover:text-[#A78BFA] hover:border-[#A78BFA]/40 transition-colors"
              >
                <ImagePlus className="w-5 h-5" />
                <span className="text-[10px]">Photo<br />(optional)</span>
              </button>
            )}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="manual-add-photo-input"
              onChange={(e) => { if (e.target.files?.[0]) setPhoto(e.target.files[0]); e.target.value = ""; }}
            />
            <div className="flex-1 space-y-3">
              <div>
                <label className={fieldLabel}>Card name *</label>
                <Input data-testid="manual-add-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Charizard V" className={fieldInput} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={fieldLabel}>Set / series</label>
                  <Input data-testid="manual-add-set-input" value={setNameField} onChange={(e) => setSetNameField(e.target.value)} placeholder="Champion's Path" className={fieldInput} />
                </div>
                <div>
                  <label className={fieldLabel}>Card number</label>
                  <Input data-testid="manual-add-number-input" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} placeholder="079/073" className={fieldInput} />
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={fieldLabel}>Game type</label>
              <Select value={gameType} onValueChange={setGameType}>
                <SelectTrigger data-testid="manual-add-gametype-select" className={fieldInput}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pokemon">Pokemon</SelectItem>
                  <SelectItem value="mtg">Magic: The Gathering</SelectItem>
                  <SelectItem value="sports">Sports</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={fieldLabel}>Condition</label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger data-testid="manual-add-condition-select" className={fieldInput}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={fieldLabel}>Quantity owned</label>
              <Input data-testid="manual-add-quantity-input" type="number" min="1" step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className={fieldInput} />
            </div>
            <div>
              <label className={fieldLabel}>Price (optional, USD)</label>
              <Input data-testid="manual-add-price-input" type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Leave blank for AI" className={fieldInput} />
            </div>
          </div>

          <div>
            <label className={fieldLabel}>Collection</label>
            <Select value={collectionId} onValueChange={setCollectionId}>
              <SelectTrigger data-testid="manual-add-collection-select" className={fieldInput}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Uncategorized</SelectItem>
                {collections.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Button
            data-testid="manual-add-save-button"
            onClick={save}
            disabled={saving}
            className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] rounded-full disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save to binder"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
